import {
  ContactCreditListQueryVariables,
  QueryContactCreditsOrderByColumn,
  SortOrder,
} from "@/gql/graphql";
import { formatCurrency } from "@/lib/Formatters/formatCurrency";
import {
  ContactCreditListItem,
  contactCreditListQuery,
} from "@/app/ContactCredits/GraphQL/contactCreditListQuery";
import { Badge } from "@/components/catalyst/badge";
import { contactCreditTypeColorMap } from "@/app/ContactCredits/Utils/contactCreditTypeColorMap";
import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import type { TableId } from "@/app/Common/Utils/table-id";
import { useContactCreditActions } from "@/app/ContactCredits/Hooks/useContactCreditActions";
import { ReactNode } from "react";
import { TimeSinceNowCell } from "@/lib/Components/DataTable/TimeSinceNowCell";
import { Text } from "@/components/catalyst/text";
import { Link } from "@/components/catalyst/link";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";

type ContactCreditTableProps = {
  queryVariables?: Partial<ContactCreditListQueryVariables>;
  id: TableId;
  title?: string;
  rightButtons?: ReactNode;
};

type ContactCreditTableColumnId =
  | "reference"
  | "type"
  | "amount"
  | "description"
  | "against"
  | "created_at";

export function ContactCreditTable({
  queryVariables,
  id,
  title,
  rightButtons,
}: ContactCreditTableProps) {
  "use no memo";
  const getActions = useContactCreditActions();

  const columns: DataTableColDef<
    ContactCreditListItem,
    any,
    ContactCreditTableColumnId
  >[] = [
    {
      id: "reference",
      header: "Entry",
      accessorFn: (row) => row.reference,
    },
    {
      id: "type",
      header: "Type",
      accessorFn: (row) => row,
      cell: ({ row }) => (
        <Badge color={contactCreditTypeColorMap[row.original.type]}>
          {row.original.type}
        </Badge>
      ),
    },
    {
      id: "amount",
      header: "Amount",
      accessorFn: (row) =>
        formatCurrency(row.amount, row.currency, {
          showZero: true,
          allowNegative: true,
        }),
    },
    {
      id: "description",
      header: "Reason",
      accessorFn: (row) => row.description,
      cell: ({ row }) => <Text>{row.original.description}</Text>,
    },
    {
      id: "against",
      header: "Against",
      accessorFn: (row) => row,
      cell: ({ row }) => {
        const invoice = row.original.invoicePayment?.invoice;

        if (invoice) {
          return (
            <Link
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/invoices/$id"
              params={(prev) => ({ ...prev, id: invoice.id })}
              className="text-blue-500 underline"
            >
              {invoice.reference}
            </Link>
          );
        }

        if (row.original.attributedTo?.__typename === "Booking") {
          const booking = row.original.attributedTo;
          return (
            <ModelLinkCell
              from="/$tenant/$orgId/supplier/{-$supplierId}"
              to="/$tenant/$orgId/supplier/{-$supplierId}/bookings/$bookingId"
              params={(prev) => ({ ...prev, bookingId: booking.id })}
            >
              {booking.reference}
            </ModelLinkCell>
          );
        }

        return <div>--</div>;
      },
    },
    {
      id: "created_at",
      header: "Created",
      accessorFn: (row) => row.created_at,
      cell: TimeSinceNowCell,
    },
  ];

  return (
    <DataTable
      title={title}
      rightButtons={rightButtons}
      document={contactCreditListQuery}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          orderBy: [
            {
              column: QueryContactCreditsOrderByColumn.Id,
              order: SortOrder.Desc,
            },
          ],
          ...queryVariables,
        };
      }}
      getActions={getActions}
      accessor={(data) => data.contactCredits}
      columns={columns}
      id={id}
    />
  );
}
